import { Link } from 'react-router-dom';
import { InfoCircleTwoTone } from '@ant-design/icons';
import { Checkbox, Row, Tag, Tooltip } from 'antd';
import { StoreValue } from 'antd/es/form/interface';
import { ColumnsType } from 'antd/lib/table';

import { FetchImage, MilesImage, Status } from 'components';

import { getFormattedCurrency, getFormattedDate } from 'utils';

import { TColumn } from 'types';

export const getColumnByType = <T, >(columns?: TColumn<T>[]) => columns?.reduce<ColumnsType<T>>((acc, column) => {
  if (column.hidden) {
    return acc;
  }

  if (typeof column.dataIndex === 'string' && column.dataIndex?.includes('.')) {
    column.dataIndex = column.dataIndex.split('.');
  }

  if (column.sorter && column.sortOrderInfo) {
    column.sortOrder = column.sortOrderInfo?.field === column.dataIndex ? column.sortOrderInfo.order : null;
  }

  if (column?.titleInfo) {
    column.title = () => (
      <>
        {column.titleInfo?.title}

        <Tooltip title={column.titleInfo?.info}>
          <InfoCircleTwoTone className='ml-2' />
        </Tooltip>
      </>
    );
  }

  switch (column.type) {
    case TColumn.type.IDRedirect:
      acc.push({
        ...column,
        render: (id) => (
          <Link
            to={`${column.pathname || window.location.pathname}/${id}`}
            state={{ redirectFrom: `${column.pathname || window.location.pathname}${window.location.search}` }}
          >
            {id}
          </Link>
        ),
      });
      break;
    case TColumn.type.Ellipsis:
      acc.push({
        ...column,
        ellipsis: {
          showTitle: false,
        },
        render: (value) => {
          if (value) {
            return (
              <Tooltip placement='bottomLeft' title={value.label || value}>
                {value.label || value}
              </Tooltip>
            );
          }

          return null;
        },
      });
      break;
    case TColumn.type.Tags:
      acc.push({
        ...column,
        render: (value: StoreValue | StoreValue[]) => {
          if (Array.isArray(value)) {
            return value.map((val) => (
              <Tag key={val.label || val.name || val}>
                {val.label || val.name || val}
              </Tag>
            ));
          }

          if (typeof value === 'string') {
            return (
              <Tag key={value}>
                {value}
              </Tag>
            );
          }

          return null;
        },
      });
      break;
    case TColumn.type.Checkbox:
      acc.push({
        ...column,
        render: (checked) => <Checkbox disabled checked={checked} />,
      });
      break;
    case TColumn.type.Image:
      acc.push({
        ...column,
        render: (src) => (
          <Row justify='center'>
            <MilesImage
              src={src}
              width={50}
              height={50}
              preview={false}
            />
          </Row>
        ),
      });
      break;
    case TColumn.type.FetchImage:
      acc.push({
        ...column,
        render: (src) => (
          <Row justify='center'>
            <FetchImage
              src={src}
              width={50}
              height={50}
              preview={false}
            />
          </Row>
        ),
      });
      break;
    case TColumn.type.FetchImageByID:
      acc.push({
        ...column,
        render: (id) => (
          <Row justify='center'>
            <FetchImage
              imgId={id}
              width={50}
              height={50}
              preview={false}
            />
          </Row>
        ),
      });
      break;
    case TColumn.type.Status:
      acc.push({
        ...column,
        render: (status) => <Status status={status} />,
      });
      break;
    case TColumn.type.Currency:
      acc.push({
        ...column,
        render: (price: string) => getFormattedCurrency(price, true),
      });
      break;
    case TColumn.type.Date:
      acc.push({
        ...column,
        render: (value) => getFormattedDate(value, column.dateFormat),
      });
      break;
    case TColumn.type.Percent:
      acc.push({
        ...column,
        render: (value) => value && `${(value * 100).toFixed(2)}%`,
      });
      break;
    default:
      acc.push(column);
  }

  return acc;
}, []);
